import { useEffect, useState } from "react";
import { volApi, formatVolError } from "./volApi";
import { Calendar, MapPin, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function VolunteerEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({}); // outreach_id -> bool

  const load = () => {
    setLoading(true);
    volApi.get("/volunteers/me/upcoming-events")
      .then(({ data }) => setEvents(data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const rsvp = async (event, status) => {
    setPending((p) => ({ ...p, [event.id]: true }));
    try {
      await volApi.post("/volunteers/me/rsvp", { outreach_id: event.id, status });
      toast.success(status === "going" ? `RSVP'd to ${event.title}` : `Cancelled RSVP`);
      setEvents((prev) => prev.map((e) =>
        e.id === event.id ? { ...e, i_have_rsvped: status === "going", my_rsvp_status: status } : e
      ));
    } catch (err) {
      toast.error(formatVolError(err));
    } finally {
      setPending((p) => ({ ...p, [event.id]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="volunteer-events-page">
      <div>
        <div className="hw-eyebrow mb-2">Upcoming events</div>
        <h1 className="font-display text-3xl font-semibold text-[#0F172A]">Where to help next</h1>
        <p className="text-[#334155] mt-2">
          RSVP to events you can join — we&apos;ll log your hours automatically when you show up.
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white border border-[#E5E5E0] rounded-2xl h-32 animate-pulse" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="volunteer-events-empty">
          <Calendar className="w-12 h-12 mx-auto text-[#1E3A8A] opacity-50 mb-3" strokeWidth={1.5} />
          <p className="text-[#334155]">No upcoming events on the calendar yet. Check back soon!</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="volunteer-events-list">
          {events.map((e) => (
            <li
              key={e.id}
              data-testid={`volunteer-event-${e.id}`}
              className="bg-white border border-[#E5E5E0] rounded-2xl p-5 hw-card-hover"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-wider bg-[#E8ECE7] text-[#1E3A8A] px-2 py-0.5 rounded-full">
                      {(e.activity_type || "event").replace(/_/g, " ")}
                    </span>
                    {e.i_have_rsvped && (
                      <span data-testid={`volunteer-event-rsvped-${e.id}`} className="text-[10px] uppercase tracking-wider bg-[#E8ECE7] text-[#166534] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <Check className="w-3 h-3" /> Going
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#0F172A]">{e.title}</h3>
                  <div className="text-sm text-[#334155] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" /> {e.activity_date}
                    </span>
                    {(e.location || e.county) && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {[e.location, e.county && `${e.county} County`].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </div>
                  {e.notes && (
                    <p className="text-sm text-[#334155] mt-2 whitespace-pre-wrap">{e.notes}</p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {e.i_have_rsvped ? (
                    <button
                      type="button"
                      disabled={pending[e.id]}
                      onClick={() => rsvp(e, "cancelled")}
                      data-testid={`volunteer-event-cancel-${e.id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E5E5E0] text-[#991B1B] hover:bg-[#FEE2E2] text-sm font-medium disabled:opacity-50"
                    >
                      <X className="w-3.5 h-3.5" /> Cancel RSVP
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={pending[e.id]}
                      onClick={() => rsvp(e, "going")}
                      data-testid={`volunteer-event-rsvp-${e.id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E3A8A] text-white hover:bg-[#172554] text-sm font-medium disabled:opacity-50"
                    >
                      <Check className="w-3.5 h-3.5" /> RSVP to this event
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
